import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, EmptyState, PageHeader, StatusBadge } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { superApi } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';

interface AlertItem {
  id: string;
  type: string;
  tone: 'ok' | 'warn' | 'danger' | 'info';
  title: string;
  body?: string;
  tenant_id?: number | null;
  ref_type?: string | null;
  ref_id?: string | number | null;
  created_at: string;
}

const READ_KEY = 'merkaziya-super-notifications-read';

function loadRead(): Set<string> {
  try {
    const raw = localStorage.getItem(READ_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

function saveRead(set: Set<string>) {
  try {
    localStorage.setItem(READ_KEY, JSON.stringify([...set]));
  } catch {
    /* no-op */
  }
}

const TYPE_LABEL: Record<string, string> = {
  billing: 'חיוב',
  security: 'אבטחה',
  health: 'תקינות',
  churn: 'נטישה',
};

const TONE_ICON: Record<AlertItem['tone'], string> = {
  ok: 'ok',
  warn: 'warn',
  danger: 'danger',
  info: 'info',
};

type Filter = 'all' | 'unread' | 'danger' | 'warn' | 'info';

export function SuperNotifications() {
  const [items, setItems] = useState<AlertItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [read, setRead] = useState<Set<string>>(() => loadRead());
  const [filter, setFilter] = useState<Filter>('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superApi.notifications();
      setItems(res.items as AlertItem[]);
      setLoadError(false);
    } catch {
      // Don't render the reassuring "all systems healthy" empty state on a
      // failed fetch — that's the dangerous false-positive during an incident.
      setLoadError(true);
      toast('שגיאה בטעינת התראות', 'danger');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function markRead(id: string) {
    setRead((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveRead(next);
      return next;
    });
  }

  function markAllRead() {
    const next = new Set(read);
    items.forEach((i) => next.add(i.id));
    setRead(next);
    saveRead(next);
    toast('סומנו כנקראו', 'ok');
  }

  function clearRead() {
    setRead(new Set());
    saveRead(new Set());
    toast('סטטוס נקראו אופס', 'ok');
  }

  const filtered = useMemo(() => {
    if (filter === 'all') return items;
    if (filter === 'unread') return items.filter((i) => !read.has(i.id));
    return items.filter((i) => i.tone === filter);
  }, [items, read, filter]);

  const unreadCount = items.filter((i) => !read.has(i.id)).length;
  const counts = useMemo(
    () => ({
      danger: items.filter((i) => i.tone === 'danger').length,
      warn: items.filter((i) => i.tone === 'warn').length,
      info: items.filter((i) => i.tone === 'info').length,
    }),
    [items]
  );

  if (loading) {
    return (
      <div style={{ padding: 'var(--gap)' }}>
        <Card>טוען…</Card>
      </div>
    );
  }

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader
        title={
          <>
            מרכז התראות
            {unreadCount > 0 && (
              <span
                style={{
                  marginInlineStart: 8,
                  fontSize: 'var(--t-sm)',
                  background: 'var(--danger)',
                  color: 'var(--on-accent)',
                  padding: '2px 8px',
                  borderRadius: 'var(--r-pill)',
                }}
              >
                {unreadCount} חדש
              </span>
            )}
          </>
        }
        actions={
          <>
            <button type="button" className="btn ghost" onClick={() => void load()}>
              רענן
            </button>
            {unreadCount > 0 && (
              <button type="button" className="btn" onClick={markAllRead}>
                סמן הכל כנקרא
              </button>
            )}
            {read.size > 0 && (
              <button type="button" className="btn ghost" onClick={clearRead}>
                אפס סטטוס
              </button>
            )}
          </>
        }
      />

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
          הכל ({items.length})
        </FilterChip>
        <FilterChip active={filter === 'unread'} onClick={() => setFilter('unread')}>
          לא נקראו ({unreadCount})
        </FilterChip>
        <FilterChip active={filter === 'danger'} onClick={() => setFilter('danger')} tone="danger">
          קריטי ({counts.danger})
        </FilterChip>
        <FilterChip active={filter === 'warn'} onClick={() => setFilter('warn')} tone="warn">
          אזהרה ({counts.warn})
        </FilterChip>
        <FilterChip active={filter === 'info'} onClick={() => setFilter('info')} tone="info">
          מידע ({counts.info})
        </FilterChip>
      </div>

      {filtered.length === 0 && loadError ? (
        <EmptyState
          icon="warn"
          title="שגיאה בטעינת ההתראות"
          description="לא ניתן היה לטעון את ההתראות — אל תניח שהכול תקין."
          action={<button type="button" className="btn primary" onClick={() => void load()}>נסה שוב</button>}
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="bell"
          title={filter === 'all' ? 'אין התראות פעילות' : 'אין התראות בקטגוריה זו'}
          description="כל מערכות הפלטפורמה תקינות."
        />
      ) : (
        <div className="stack">
          {filtered.map((n) => {
            const isRead = read.has(n.id);
            return (
              <Card key={n.id}>
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                    opacity: isRead ? 0.65 : 1,
                  }}
                >
                  <Icon name={TONE_ICON[n.tone]} size={20} color={`var(--${n.tone})`} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <strong style={{ fontWeight: isRead ? 400 : 600 }}>{n.title}</strong>
                      <StatusBadge tone={n.tone} size="sm">
                        {TYPE_LABEL[n.type] ?? n.type}
                      </StatusBadge>
                    </div>
                    {n.body && (
                      <div style={{ color: 'var(--text-2)', fontSize: 'var(--t-sm)', marginTop: 4 }}>{n.body}</div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: 6,
                      }}
                    >
                      <div style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>
                        {new Date(n.created_at).toLocaleString('he-IL')}
                        {n.tenant_id && (
                          <>
                            {' • '}
                            <Link to="/super/tenants" style={{ color: 'var(--muted)' }}>
                              לקוח #{n.tenant_id}
                            </Link>
                          </>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {n.ref_type === 'invoice' && (
                          <Link to="/super/invoices" className="btn ghost">
                            פתח חשבונית →
                          </Link>
                        )}
                        {n.ref_type === 'tenant' && (
                          <Link to="/super/tenants" className="btn ghost">
                            פתח לקוח →
                          </Link>
                        )}
                        {!isRead && (
                          <button type="button" className="btn ghost" onClick={() => markRead(n.id)}>
                            סמן כנקרא
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  tone,
  children,
}: {
  active: boolean;
  onClick: () => void;
  tone?: 'danger' | 'warn' | 'info';
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '4px 12px',
        borderRadius: 999,
        border: `1px solid ${active ? 'var(--accent)' : 'var(--line)'}`,
        background: active ? 'var(--accent-soft, var(--surface-2))' : 'transparent',
        color: tone ? `var(--${tone})` : undefined,
        fontSize: 'var(--t-sm)',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}
